// src/Component/SideData/Overview/AiAgentWorkFlow/utils/validationRulesApi.js
//
// ============================================================
// VALIDATION RULES — admin-side HTTP helpers
// ============================================================
//
// Two calls, both against the AutoBot backend's /validation_rules
// endpoint:
//
//   fetchRulesForForm(appName, formUuid)    → rule[] for one form
//   saveRulesForForm(appName, formUuid, rules) → upsert + version
//
// The client SDK (client_core) has its own resolver that fetches
// the *whole app's* rule set in one call (GET without form_uuid).
// That's the read path the visitor's browser uses. This module is
// only for the admin authoring surface, which edits one form at a
// time and therefore fetches / saves per-form.
//
// The backend enforces:
//   • POST /validation_rules is admin-only (requires Authorization).
//   • GET /validation_rules accepts either Authorization or x-api-key.
//
// The admin always has a token, so both calls here send the token.
//
// Shape contract (mirrors users.py::_validate_validation_rules_shape):
//
//   conditional_required:
//     { id, kind: "conditional_required",
//       when: { field, equals },
//       require: [field, ...],
//       message? }
//
//   presence_cardinality:
//     { id, kind: "presence_cardinality",
//       of: [field, ...],              # >= 2
//       at_least: <int >= 1>,
//       message? }
//
// The backend normalizes and validates on write. This module does
// NOT re-validate — the panel prevents the admin from producing
// malformed rules in the first place, and the backend is the
// authority if anything slips through.

const backend_api_base_url = `${process.env.REACT_APP_SL_BE_BASE_ENV_PART}/${process.env.REACT_APP_STAGE_ENV}`;

function buildApiBase(region) {
  const regionLower = (region || "us").toLowerCase();
  return `https://${regionLower}.autobot2.${backend_api_base_url}`;
}

function getToken() {
  return localStorage.getItem("token") || "";
}

/**
 * Fetch the rule set for a single form.
 *
 * Response (backend): { success, form_uuid, version, rules: [...] }
 *
 * Returns the rule array (possibly empty). On any failure — network,
 * auth, malformed body — logs and returns []. The panel treats an
 * empty array the same way it treats "no rules yet"; the admin can
 * add rules and save, and the save will overwrite whatever was
 * actually stored (because POST is an upsert, not a patch).
 *
 * Note: returning [] on error is deliberately NOT the same as
 * "there are no rules." If the fetch failed and the admin saves an
 * empty array, the rules get cleared. To avoid that footgun, the
 * panel disables Save until the fetch has completed at least once
 * (see loadedRef in the component).
 */
export async function fetchRulesForForm({ appName, formUuid, region }) {
  if (!appName || !formUuid) {
    console.warn(
      "[validationRulesApi] fetchRulesForForm: missing appName or formUuid",
      { appName, formUuid },
    );
    return [];
  }

  const url =
    `${buildApiBase(region)}/validation_rules` +
    `?app_name=${encodeURIComponent(appName)}` +
    `&form_uuid=${encodeURIComponent(formUuid)}`;

  try {
    const resp = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: getToken(),
      },
    });

    if (!resp.ok) {
      const text = await resp.text().catch(() => "");
      throw new Error(`HTTP ${resp.status}${text ? " – " + text : ""}`);
    }

    const body = await resp.json();

    if (!body?.success) {
      throw new Error(body?.error || "Fetch returned success: false");
    }

    const rules = Array.isArray(body.rules) ? body.rules : [];
    console.log(
      `[validationRulesApi] fetched ${rules.length} rule(s) for form_uuid=${formUuid}`,
    );
    return rules;
  } catch (err) {
    console.error("[validationRulesApi] fetchRulesForForm failed:", err);
    return [];
  }
}

/**
 * Upsert the rule set for a single form.
 *
 * Body (backend): { tenant_id, app_name, form_uuid, rules }
 * The tenant_id comes from the token on the backend side — the
 * admin surface doesn't need to send it, but the backend's POST
 * handler reads it from the decoded token and only falls back to
 * body.tenant_id if the token doesn't carry one.
 *
 * Response (backend):
 *   { success, form_uuid, version, rule_count, cleared, changed }
 *
 * `changed: false` is the backend telling us the incoming rules are
 * semantically identical to what was already stored — no write, no
 * version bump. The admin UI can surface this as "No changes to
 * save" instead of a spurious success.
 *
 * Returns the parsed response on success, or throws on failure.
 * The caller decides how to surface errors (both callers use the
 * existing ReusablePopup pattern).
 */
export async function saveRulesForForm({ appName, formUuid, rules, region }) {
  if (!appName) throw new Error("Missing appName");
  if (!formUuid) throw new Error("Missing formUuid");
  if (!Array.isArray(rules)) throw new Error("rules must be an array");

  const url = `${buildApiBase(region)}/validation_rules`;

  const resp = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: getToken(),
    },
    body: JSON.stringify({
      app_name: appName,
      form_uuid: formUuid,
      rules,
    }),
  });

  let body = null;
  try {
    body = await resp.json();
  } catch (_) {
    // Non-JSON body. Let the HTTP-status branch below handle it.
  }

  if (!resp.ok || !body?.success) {
    const msg =
      body?.error ||
      body?.detail ||
      `Save failed (HTTP ${resp.status})`;
    throw new Error(msg);
  }

  console.log(
    `[validationRulesApi] saved ${rules.length} rule(s) for form_uuid=${formUuid}`,
    { changed: body.changed, version: body.version },
  );

  return body;
}